import React, { cloneElement, useEffect, useRef } from "react";

interface IVisibilitySensorProps {
    children: React.ReactElement<{ ref?: React.Ref<HTMLElement> }>;
    onChange: (isVisible: boolean) => void;
    containment?: HTMLElement;
    delayedCall?: boolean;
    intervalDelay?: number;
}

// Replacement for react-visibility-sensor, which relies on ReactDOM.findDOMNode (removed in React 19).
// Keeps the same behaviour: polls on an interval and calls onChange whenever the child
// becomes fully visible inside the containment (or window) or stops being so.
const VisibilitySensor = ({ children, onChange, containment, delayedCall = false, intervalDelay = 100 }: IVisibilitySensorProps) => {
    const nodeRef = useRef<HTMLElement>(null);
    const isVisibleRef = useRef<boolean | null>(null);
    const onChangeRef = useRef(onChange);
    const containmentRef = useRef(containment);
    onChangeRef.current = onChange;
    containmentRef.current = containment;

    useEffect(() => {
        const check = () => {
            const node = nodeRef.current;
            if (!node) {
                return;
            }

            const nodeRect = node.getBoundingClientRect();
            const top = Math.floor(nodeRect.top);
            const left = Math.floor(nodeRect.left);
            const bottom = Math.floor(nodeRect.bottom);
            const right = Math.floor(nodeRect.right);

            const containmentRect = containmentRef.current
                ? containmentRef.current.getBoundingClientRect()
                : {
                    top: 0,
                    left: 0,
                    bottom: window.innerHeight || document.documentElement.clientHeight,
                    right: window.innerWidth || document.documentElement.clientWidth,
                };

            const hasSize = bottom - top > 0 && right - left > 0;
            const isVisible = hasSize
                && top >= containmentRect.top
                && left >= containmentRect.left
                && bottom <= containmentRect.bottom
                && right <= containmentRect.right;

            if (isVisibleRef.current !== isVisible) {
                isVisibleRef.current = isVisible;
                onChangeRef.current(isVisible);
            }
        };

        const intervalId = window.setInterval(check, intervalDelay);
        if (!delayedCall) {
            check();
        }

        return () => window.clearInterval(intervalId);
    }, [delayedCall, intervalDelay]);

    return cloneElement(children, { ref: nodeRef });
};

export default VisibilitySensor;
